<script setup>
defineProps({
    /**
     * Heading shown in the page hero.
     */
    title: { type: String, required: true },
    /**
     * ISO date string for "last updated" — rendered as Czech long form.
     */
    updatedAt: { type: String, required: true },
})

const formattedDate = (iso) =>
    new Intl.DateTimeFormat('cs-CZ', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso))
</script>

<template>
    <article class="bg-white pt-32 pb-20 md:pt-40 md:pb-28">
        <div class="mx-auto max-w-3xl px-6 lg:px-10">
            <p class="text-xs font-semibold tracking-[0.18em] text-zinc-500 uppercase">Právní informace</p>

            <h1
                class="text-brand-900 mt-4 text-4xl leading-[1.1] font-extrabold tracking-tight sm:text-5xl md:text-6xl"
            >
                {{ title }}
            </h1>

            <p class="mt-6 text-sm text-zinc-500">Naposledy aktualizováno: {{ formattedDate(updatedAt) }}</p>

            <div class="legal-body mt-12 text-base leading-relaxed text-zinc-700">
                <slot />
            </div>
        </div>
    </article>
</template>

<style scoped>
.legal-body :deep(h2) {
    color: var(--color-brand-900);
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 800;
    letter-spacing: -0.025em;
    margin-top: 3rem;
    margin-bottom: 1rem;
}

@media (min-width: 768px) {
    .legal-body :deep(h2) {
        font-size: 1.875rem;
        line-height: 2.25rem;
    }
}

.legal-body :deep(h3) {
    color: var(--color-brand-900);
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 800;
    letter-spacing: -0.025em;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
}

.legal-body :deep(p) {
    margin-top: 1rem;
}

.legal-body :deep(ul),
.legal-body :deep(ol) {
    margin-top: 1rem;
    padding-left: 1.5rem;
}

.legal-body :deep(ul) > li,
.legal-body :deep(ol) > li {
    margin-top: 0.5rem;
}

.legal-body :deep(ul) {
    list-style-type: disc;
}

.legal-body :deep(ol) {
    list-style-type: decimal;
}

.legal-body :deep(a) {
    color: var(--color-brand-900);
    font-weight: 500;
    text-underline-offset: 4px;
}

.legal-body :deep(a:hover) {
    text-decoration: underline;
}

.legal-body :deep(strong) {
    color: var(--color-brand-900);
    font-weight: 600;
}
</style>
